/**
 * PhotoService - Unified photo data management
 * 
 * Single source of truth for all photo data loading, caching, and updates.
 * Handles basic metadata vs full photo data, request deduplication, and processing status polling.
 * 
 * Events emitted:
 * - 'photoUpdated' - When photo data changes (detail: { photo, changes })
 * - 'photosLoaded' - When batch of photos loaded (detail: { photos, hasFullData })
 * - 'pollingComplete' - When all photos in folder finish processing (detail: { folderId })
 */

export class PhotoService extends EventTarget {
  constructor(maxCacheSize = 1000) {
    super();
    
    this.maxCacheSize = maxCacheSize;
    
    // Main cache: photoId -> { photo, hasFullData, lastAccessed }
    this.cache = new Map();
    this.accessOrder = []; // Track LRU order
    
    // Track which folders have had their basic metadata fully loaded
    this.foldersWithBasicInfo = new Set(); // folderId
    
    // Track photo ordering per folder (as returned by server)
    this.folderPhotoOrder = new Map(); // folderId -> [photoId1, photoId2, ...]
    
    // Request deduplication
    this.pendingBasicRequests = new Map(); // key -> Promise
    this.pendingFullRequests = new Map(); // key -> Promise
    
    // Processing status polling
    this.pollingIntervals = new Map(); // folderId -> intervalId
    this.pollingIntervalMs = 3000; // Poll every 3 seconds
    
    // Track which folder each photo belongs to for efficient polling
    this.photoToFolder = new Map(); // photoId -> folderId
  }
  
  /**
   * Get basic photo info (id, filename, thumb_256_url, processing_status)
   * Returns immediately with cached data if available, otherwise fetches
   * @param {number} folderId - Folder ID to load from
   * @param {Array<number>} photoIds - Optional specific photo IDs, or null to load all in folder
   * @returns {Promise<Array<object>>} - Array of basic photo metadata
   */
  async getBasicInfo(folderId, photoIds = null) {
    // If photoIds provided, check cache first
    if (photoIds && photoIds.length > 0) {
      const uncachedIds = photoIds.filter(id => !this.cache.has(id));
      
      if (uncachedIds.length === 0) {
        // All in cache, return immediately
        return photoIds.map(id => this._get(id).photo);
      }
      
      // Need to fetch - but we fetch the whole folder for basic info
      // since the endpoint is /api/v1/folders/:id/photos
      return await this._fetchBasicInfo(folderId);
    }
    
    // No specific IDs - fetch all for folder
    return await this._fetchBasicInfo(folderId);
  }
  
  /**
   * Fetch basic info from server
   * @private
   */
  async _fetchBasicInfo(folderId) {
    const requestKey = `folder_${folderId}`;
    
    // Check if already fetching
    if (this.pendingBasicRequests.has(requestKey)) {
      console.log(`[PhotoService] Basic info already fetching for folder ${folderId}, waiting...`);
      return await this.pendingBasicRequests.get(requestKey);
    }
    
    // Create fetch promise
    const fetchPromise = (async () => {
      try {
        console.log(`[PhotoService] Fetching basic info for folder ${folderId}`);
        const response = await fetch(`/api/v1/folders/${folderId}/photos`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch basic info: ${response.status}`);
        }
        
        const photos = await response.json();
        
        // Mark this folder as having its basic info loaded
        this.foldersWithBasicInfo.add(folderId);
        
        // Store the photo order for this folder
        this.folderPhotoOrder.set(folderId, photos.map(p => p.id));
        
        // Store in cache (as basic data)
        // BUT don't downgrade if photo already has full data
        photos.forEach(photo => {
          const existing = this.cache.get(photo.id);
          // Only set if not already cached, or if cached but doesn't have full data
          if (!existing || !existing.hasFullData) {
            this._set(photo.id, photo, false); // hasFullData = false
            this.photoToFolder.set(photo.id, folderId);
          } else {
            // Photo already has full data, just update the folder mapping
            this.photoToFolder.set(photo.id, folderId);
            console.log(`[PhotoService] Skipping basic data for photo ${photo.id} (already has full data)`);
          }
        });
        
        // Emit event
        this.dispatchEvent(new CustomEvent('photosLoaded', {
          detail: { photos, hasFullData: false, folderId }
        }));
        
        console.log(`[PhotoService] Loaded ${photos.length} basic photos for folder ${folderId}`);
        
        return photos;
      } catch (error) {
        console.error('[PhotoService] Error fetching basic info:', error);
        throw error;
      } finally {
        this.pendingBasicRequests.delete(requestKey);
      }
    })();
    
    this.pendingBasicRequests.set(requestKey, fetchPromise);
    return fetchPromise;
  }
  
  /**
   * Get full photo data (includes all sliders, settings, etc.)
   * Returns immediately with cached data if available, otherwise fetches
   * @param {Array<number>} photoIds - Array of photo IDs to load
   * @returns {Promise<Array<object>>} - Array of full photo data
   */
  async getFullInfo(photoIds) {
    if (!photoIds || photoIds.length === 0) {
      return [];
    }
    
    // Check which ones we need to fetch
    const needToFetch = [];
    const cached = [];
    
    photoIds.forEach(id => {
      const entry = this.cache.get(id);
      if (entry && entry.hasFullData) {
        cached.push(entry.photo);
        this._updateAccessOrder(id);
      } else {
        needToFetch.push(id);
      }
    });
    
    if (needToFetch.length === 0) {
      console.log(`[PhotoService] All ${photoIds.length} photos already have full data`);
      return photoIds.map(id => this._get(id).photo);
    }
    
    // Fetch missing ones
    console.log(`[PhotoService] Need to fetch full data for ${needToFetch.length}/${photoIds.length} photos`);
    const fetched = await this._fetchFullInfo(needToFetch);
    
    // Return all requested photos in original order
    return photoIds.map(id => this._get(id).photo);
  }
  
  /**
   * Fetch full info from server in batches
   * @private
   */
  async _fetchFullInfo(photoIds, batchSize = 50) {
    const allPhotos = [];
    
    // Split into batches
    for (let i = 0; i < photoIds.length; i += batchSize) {
      const batch = photoIds.slice(i, i + batchSize);
      const batchKey = batch.sort((a, b) => a - b).join(',');
      
      // Check if this batch is already being fetched
      if (this.pendingFullRequests.has(batchKey)) {
        console.log(`[PhotoService] Batch already fetching, waiting...`);
        const photos = await this.pendingFullRequests.get(batchKey);
        allPhotos.push(...photos);
        continue;
      }
      
      // Create fetch promise
      const fetchPromise = (async () => {
        try {
          console.log(`[PhotoService] Fetching full data for ${batch.length} photos`);
          const response = await fetch('/api/v1/photos/batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ photo_ids: batch })
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch full info: ${response.status}`);
          }
          
          const photos = await response.json();
          
          // Store in cache (as full data)
          photos.forEach(photo => {
            const oldPhoto = this.cache.get(photo.id)?.photo;
            this._set(photo.id, photo, true); // hasFullData = true
            
            if (photo.folder_id) {
              this.photoToFolder.set(photo.id, photo.folder_id);
            }
            
            // Emit update event for each photo
            if (oldPhoto) {
              const changes = this._detectChanges(oldPhoto, photo);
              if (changes.length > 0) {
                this.dispatchEvent(new CustomEvent('photoUpdated', {
                  detail: { photo, changes }
                }));
              }
            }
          });
          
          // Emit batch loaded event
          this.dispatchEvent(new CustomEvent('photosLoaded', {
            detail: { photos, hasFullData: true }
          }));
          
          console.log(`[PhotoService] Loaded ${photos.length} full photos`);
          
          return photos;
        } catch (error) {
          console.error('[PhotoService] Error fetching full info:', error);
          throw error;
        } finally {
          this.pendingFullRequests.delete(batchKey);
        }
      })();
      
      this.pendingFullRequests.set(batchKey, fetchPromise);
      const photos = await fetchPromise;
      allPhotos.push(...photos);
    }
    
    return allPhotos;
  }
  
  /**
   * Get photo by ID (synchronous cache lookup)
   * Returns whatever data is available (basic or full)
   * @param {number} photoId - Photo ID
   * @returns {object|null} - Photo data or null if not cached
   */
  getPhotoById(photoId) {
    const entry = this.cache.get(photoId);
    if (entry) {
      this._updateAccessOrder(photoId);
      return entry.photo;
    }
    return null;
  }
  
  /**
   * Check if photo has full data loaded
   * @param {number} photoId - Photo ID
   * @returns {boolean}
   */
  hasFullData(photoId) {
    const entry = this.cache.get(photoId);
    return entry ? entry.hasFullData : false;
  }
  
  /**
   * Check if a folder's basic metadata has been loaded
   * @param {number} folderId - Folder ID
   * @returns {boolean}
   */
  hasFolderBasicInfo(folderId) {
    return this.foldersWithBasicInfo.has(folderId);
  }
  
  /**
   * Get all photos for a folder (from cache only)
   * Returns photos in the order they were received from the server
   * @param {number} folderId - Folder ID
   * @returns {Array<object>} - Array of cached photos for this folder
   */
  getPhotosForFolder(folderId) {
    const photoOrder = this.folderPhotoOrder.get(folderId);
    
    // If we have the order from the server, use it
    if (photoOrder) {
      const photos = [];
      for (const photoId of photoOrder) {
        const entry = this.cache.get(photoId);
        if (entry) {
          photos.push(entry.photo);
        }
      }
      return photos;
    }
    
    // Fallback: return in arbitrary order (for photos loaded before folder metadata)
    const photos = [];
    for (const [photoId, fId] of this.photoToFolder.entries()) {
      if (fId === folderId) {
        const entry = this.cache.get(photoId);
        if (entry) {
          photos.push(entry.photo);
        }
      }
    }
    return photos;
  }
  
  /**
   * Precache photos for viewport (load full data for visible range + buffer)
   * @param {Array<object>} allPhotoMetadata - All photo metadata for folder
   * @param {number} startIndex - First visible photo index
   * @param {number} endIndex - Last visible photo index
   * @param {number} buffer - Buffer percentage (default 0.3 = 30%)
   * @returns {Promise<Array<object>>}
   */
  async precachePhotosForViewport(allPhotoMetadata, startIndex, endIndex, buffer = 0.3) {
    if (allPhotoMetadata.length === 0) return [];
    
    const totalPhotos = allPhotoMetadata.length;
    const visibleCount = endIndex - startIndex + 1;
    
    // Calculate buffer size
    const bufferSize = Math.ceil(visibleCount * buffer);
    
    // Calculate extended range
    const extendedStart = Math.max(0, startIndex - bufferSize);
    const extendedEnd = Math.min(totalPhotos - 1, endIndex + bufferSize);
    
    // Get photo IDs in range that need full data
    const photoIdsToLoad = [];
    for (let i = extendedStart; i <= extendedEnd; i++) {
      const photoId = allPhotoMetadata[i].id;
      const entry = this.cache.get(photoId);
      if (!entry || !entry.hasFullData) {
        photoIdsToLoad.push(photoId);
      }
    }
    
    if (photoIdsToLoad.length === 0) {
      console.log(`[PhotoService] All viewport photos already have full data`);
      return [];
    }
    
    console.log(`[PhotoService] Precaching ${photoIdsToLoad.length} photos for viewport`);
    return await this.getFullInfo(photoIdsToLoad);
  }
  
  /**
   * Update a photo in cache (used for upload progress, etc.)
   * @param {number} photoId - Photo ID
   * @param {object} updates - Partial photo data to merge
   */
  updatePhoto(photoId, updates) {
    const entry = this.cache.get(photoId);
    if (!entry) {
      console.warn(`[PhotoService] Cannot update non-existent photo ${photoId}`);
      return;
    }
    
    const oldPhoto = entry.photo;
    const newPhoto = { ...oldPhoto, ...updates };
    
    // Update cache
    this._set(photoId, newPhoto, entry.hasFullData);
    
    // Detect changes and emit event
    const changes = this._detectChanges(oldPhoto, newPhoto);
    if (changes.length > 0) {
      this.dispatchEvent(new CustomEvent('photoUpdated', {
        detail: { photo: newPhoto, changes }
      }));
    }
  }
  
  /**
   * Add a new photo to cache
   * @param {object} photo - Photo data
   * @param {boolean} hasFullData - Whether this is full data or basic metadata
   */
  addPhoto(photo, hasFullData = false) {
    this._set(photo.id, photo, hasFullData);
    
    if (photo.folder_id) {
      this.photoToFolder.set(photo.id, photo.folder_id);
    }
    
    this.dispatchEvent(new CustomEvent('photosLoaded', {
      detail: { photos: [photo], hasFullData }
    }));
  }
  
  /**
   * Remove photo from cache
   * @param {number} photoId - Photo ID
   */
  removePhoto(photoId) {
    this.cache.delete(photoId);
    this.photoToFolder.delete(photoId);
    this.accessOrder = this.accessOrder.filter(id => id !== photoId);
  }
  
  /**
   * Start polling for processing status updates
   * Automatically determines which photos need polling based on processing_status
   * @param {number} folderId - Folder ID to poll
   */
  startPolling(folderId) {
    // Don't start if already polling
    if (this.pollingIntervals.has(folderId)) {
      return;
    }
    
    // Check if folder has processing photos
    if (!this._hasProcessingPhotos(folderId)) {
      console.log(`[PhotoService] No processing photos in folder ${folderId}, skipping polling`);
      return;
    }
    
    console.log(`[PhotoService] Starting polling for folder ${folderId}`);
    
    const intervalId = setInterval(async () => {
      await this._pollFolder(folderId);
    }, this.pollingIntervalMs);
    
    this.pollingIntervals.set(folderId, intervalId);
  }
  
  /**
   * Stop polling for a folder
   * @param {number} folderId - Folder ID
   */
  stopPolling(folderId) {
    const intervalId = this.pollingIntervals.get(folderId);
    if (intervalId) {
      console.log(`[PhotoService] Stopping polling for folder ${folderId}`);
      clearInterval(intervalId);
      this.pollingIntervals.delete(folderId);
    }
  }
  
  /**
   * Poll folder for processing status updates
   * @private
   */
  async _pollFolder(folderId) {
    try {
      // Fetch latest basic metadata
      const response = await fetch(`/api/v1/folders/${folderId}/photos`);
      
      if (!response.ok) {
        console.error(`[PhotoService] Polling failed: ${response.status}`);
        return;
      }
      
      const updatedPhotos = await response.json();
      
      // Check for changes
      let hasChanges = false;
      updatedPhotos.forEach(updated => {
        const entry = this.cache.get(updated.id);
        if (entry) {
          const oldPhoto = entry.photo;
          const statusChanged = oldPhoto.processing_status !== updated.processing_status;
          const urlChanged = oldPhoto.thumb_256_url !== updated.thumb_256_url;
          
          if (statusChanged || urlChanged) {
            if (statusChanged) {
              console.log(`[PhotoService] Photo ${updated.id} status: ${oldPhoto.processing_status} → ${updated.processing_status}`);
            }
            if (urlChanged) {
              console.log(`[PhotoService] Photo ${updated.id} thumb_256_url updated`);
            }
            
            // Merge updates into cached photo
            const mergedPhoto = { ...oldPhoto, ...updated };
            this._set(updated.id, mergedPhoto, entry.hasFullData);
            
            const changes = this._detectChanges(oldPhoto, mergedPhoto);
            if (changes.length > 0) {
              this.dispatchEvent(new CustomEvent('photoUpdated', {
                detail: { photo: mergedPhoto, changes }
              }));
            }
            
            hasChanges = true;
          }
        }
      });
      
      // Check if polling should stop
      if (!this._hasProcessingPhotos(folderId)) {
        console.log(`[PhotoService] All photos in folder ${folderId} finished processing`);
        this.stopPolling(folderId);
        
        this.dispatchEvent(new CustomEvent('pollingComplete', {
          detail: { folderId }
        }));
      }
    } catch (error) {
      console.error('[PhotoService] Error polling folder:', error);
    }
  }
  
  /**
   * Check if folder has any processing photos
   * @private
   */
  _hasProcessingPhotos(folderId) {
    for (const [photoId, fId] of this.photoToFolder.entries()) {
      if (fId === folderId) {
        const entry = this.cache.get(photoId);
        if (entry) {
          const status = entry.photo.processing_status;
          if (status === 'pending' || status === 'processing') {
            return true;
          }
        }
      }
    }
    return false;
  }
  
  /**
   * Detect changes between old and new photo
   * @private
   */
  _detectChanges(oldPhoto, newPhoto) {
    const changes = [];
    const keys = ['processing_status', 'thumb_256_url', 'thumb_512_url', 'thumb_1024_url'];
    
    keys.forEach(key => {
      if (oldPhoto[key] !== newPhoto[key]) {
        changes.push(key);
      }
    });
    
    return changes;
  }
  
  /**
   * Set photo in cache
   * @private
   */
  _set(photoId, photo, hasFullData) {
    // Check if need to evict
    if (!this.cache.has(photoId) && this.cache.size >= this.maxCacheSize) {
      this._evictLRU();
    }
    
    // Update or add
    if (this.cache.has(photoId)) {
      this._updateAccessOrder(photoId);
    } else {
      this.accessOrder.push(photoId);
    }
    
    this.cache.set(photoId, {
      photo,
      hasFullData,
      lastAccessed: Date.now()
    });
  }
  
  /**
   * Get photo entry from cache
   * @private
   */
  _get(photoId) {
    return this.cache.get(photoId);
  }
  
  /**
   * Update access order for LRU
   * @private
   */
  _updateAccessOrder(photoId) {
    this.accessOrder = this.accessOrder.filter(id => id !== photoId);
    this.accessOrder.push(photoId);
    
    const entry = this.cache.get(photoId);
    if (entry) {
      entry.lastAccessed = Date.now();
    }
  }
  
  /**
   * Evict least recently used photo
   * @private
   */
  _evictLRU() {
    if (this.accessOrder.length === 0) return;
    
    const oldestId = this.accessOrder.shift();
    this.cache.delete(oldestId);
    this.photoToFolder.delete(oldestId);
    
    console.log(`[PhotoService] Evicted photo ${oldestId} (LRU)`);
  }
  
  /**
   * Get cache statistics
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      maxCacheSize: this.maxCacheSize,
      utilizationPercent: Math.round((this.cache.size / this.maxCacheSize) * 100),
      pendingBasicRequests: this.pendingBasicRequests.size,
      pendingFullRequests: this.pendingFullRequests.size,
      activePolling: this.pollingIntervals.size
    };
  }
  
  /**
   * Clear cache
   */
  clear() {
    this.cache.clear();
    this.accessOrder = [];
    this.photoToFolder.clear();
    this.pendingBasicRequests.clear();
    this.pendingFullRequests.clear();
    
    // Stop all polling
    for (const folderId of this.pollingIntervals.keys()) {
      this.stopPolling(folderId);
    }
  }
}

